import {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {DopamineChatStreamEvent} from 'interfaces'
import {DopamineAssistantService} from 'services/api'
import {
  createDopamineAnswer,
  updateDopamineAnswer,
} from '../DopamineAssistantAnswer.model'
import {mapChatEvent} from '../DopamineAssistantEvent.model'
import {
  createDopamineRunProgress,
  updateDopamineRunProgress,
} from '../DopamineAssistantProgress.model'
import {
  DopamineAssistantTurn,
  DopaminePromptSubmission,
  DopamineRunEvent,
} from '../DopamineAssistant.types'

type UseDopamineAssistantRunOptions = {
  clientSessionId: string
  conversationRevision: number
  onSessionExpired(): void
  onSessionSettled(): void
  onSessionStarted(): void
}

type UseDopamineAssistantRun = {
  isRunning: boolean
  queuedTurn: DopamineAssistantTurn | null
  removeQueuedTurn(turnId: string): void
  submitPrompt(submission: DopaminePromptSubmission): void
  turns: DopamineAssistantTurn[]
}

type QueuedTurn = Pick<DopamineAssistantTurn, 'id' | 'submission'>

type StreamRunState = {
  activeMessageId: string | null
  hasRunStarted: boolean
  hasTerminalEvent: boolean
  isSessionExpired: boolean
  runId: string
  threadId: string
}

type HandleChatEventOptions = {
  event: DopamineChatStreamEvent
  onEvent(event: DopamineRunEvent): void
  onSessionExpired(): void
  onSessionStarted(): void
  runErrorMessage: string
  sessionEndedMessage: string
  state: StreamRunState
}

type BufferedTextEvent = {
  event: Extract<DopamineRunEvent, {type: 'TEXT_MESSAGE_CONTENT'}>
  turnId: string
}

type BufferedTextEventGroup = {
  deltas: string[]
  event: BufferedTextEvent['event']
  turnId: string
}

const STREAM_RENDER_INTERVAL_MS = 50

const createTurn = ({id, submission}: QueuedTurn): DopamineAssistantTurn => ({
  answer: createDopamineAnswer(),
  id,
  progress: createDopamineRunProgress(),
  status: 'queued',
  submission,
})

const getTurnStatus = (
  currentStatus: DopamineAssistantTurn['status'],
  event: DopamineRunEvent,
): DopamineAssistantTurn['status'] => {
  switch (event.type) {
    case 'RUN_STARTED':
      return 'active'
    case 'RUN_FINISHED':
      return 'completed'
    case 'RUN_ERROR':
      return 'failed'
    default:
      return currentStatus
  }
}

const updateTurn = (
  turn: DopamineAssistantTurn,
  event: DopamineRunEvent,
): DopamineAssistantTurn => ({
  ...turn,
  answer: updateDopamineAnswer(turn.answer, event),
  progress: updateDopamineRunProgress(turn.progress, event),
  status: getTurnStatus(turn.status, event),
})

const assertChatEvent = (condition: boolean, message: string): void => {
  if (!condition) throw new Error(message)
}

const validateChatEvent = (
  event: DopamineChatStreamEvent,
  state: StreamRunState,
): void => {
  if (event.type === 'RUN_STARTED') {
    assertChatEvent(
      !state.hasRunStarted,
      'Dopamine chat run started more than once',
    )
    state.hasRunStarted = true
    state.runId = event.runId
    state.threadId = event.threadId
    return
  }

  assertChatEvent(
    state.hasRunStarted || event.type === 'RUN_ERROR',
    'Dopamine chat run started event is missing',
  )

  switch (event.type) {
    case 'RUN_FINISHED':
      assertChatEvent(
        event.runId === state.runId,
        'Dopamine chat run ID changed',
      )
      assertChatEvent(
        event.threadId === state.threadId,
        'Dopamine chat thread ID changed',
      )
      assertChatEvent(
        state.activeMessageId === null,
        'Dopamine chat run finished before the active message ended',
      )
      return
    case 'TEXT_MESSAGE_START':
      assertChatEvent(
        state.activeMessageId === null,
        'Dopamine chat message started before the prior message ended',
      )
      state.activeMessageId = event.messageId
      return
    case 'TEXT_MESSAGE_CONTENT':
      assertChatEvent(
        event.messageId === state.activeMessageId,
        'Dopamine chat message ID changed',
      )
      return
    case 'TEXT_MESSAGE_END':
      assertChatEvent(
        event.messageId === state.activeMessageId,
        'Dopamine chat message ID changed',
      )
      state.activeMessageId = null
      return
    default:
      return
  }
}

const handleChatEvent = ({
  event,
  onEvent,
  onSessionExpired,
  onSessionStarted,
  runErrorMessage,
  sessionEndedMessage,
  state,
}: HandleChatEventOptions): void => {
  validateChatEvent(event, state)
  const isSessionExpired =
    event.type === 'RUN_ERROR' && event.code === 'SESSION_EXPIRED'

  if (event.type === 'RUN_STARTED') onSessionStarted()

  onEvent(
    mapChatEvent({
      event,
      runErrorMessage: isSessionExpired ? sessionEndedMessage : runErrorMessage,
      runId: state.runId,
    }),
  )

  if (event.type === 'RUN_FINISHED' || event.type === 'RUN_ERROR') {
    state.hasTerminalEvent = true
  }
  if (isSessionExpired) {
    state.isSessionExpired = true
    onSessionExpired()
  }
}

const coalesceTextEvents = (
  events: BufferedTextEvent[],
): BufferedTextEvent[] => {
  const groups = events.reduce<BufferedTextEventGroup[]>((result, current) => {
    const previousGroup = result.at(-1)
    if (
      previousGroup?.turnId === current.turnId &&
      previousGroup.event.messageId === current.event.messageId &&
      previousGroup.event.runId === current.event.runId
    ) {
      previousGroup.deltas.push(current.event.delta)
      return result
    }

    result.push({
      deltas: [current.event.delta],
      event: current.event,
      turnId: current.turnId,
    })
    return result
  }, [])

  return groups.map(({deltas, event, turnId}) => ({
    event: {...event, delta: deltas.join('')},
    turnId,
  }))
}

export const useDopamineAssistantRun = ({
  clientSessionId,
  conversationRevision,
  onSessionExpired,
  onSessionSettled,
  onSessionStarted,
}: UseDopamineAssistantRunOptions): UseDopamineAssistantRun => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const [turns, setTurns] = useState<DopamineAssistantTurn[]>([])
  const queueRef = useRef<QueuedTurn[]>([])
  const isProcessingRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const bufferedTextEventsRef = useRef<BufferedTextEvent[]>([])
  const bufferedTextFlushIdRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const generationRef = useRef(0)
  const nextTurnIdRef = useRef(1)

  const applyEvents = useCallback(
    (turnId: string, events: DopamineRunEvent[]): void => {
      setTurns(currentTurns =>
        currentTurns.map(turn =>
          turn.id === turnId
            ? events.reduce(
                (updatedTurn, event) => updateTurn(updatedTurn, event),
                turn,
              )
            : turn,
        ),
      )
    },
    [],
  )

  const flushBufferedTextEvents = useCallback((): void => {
    bufferedTextFlushIdRef.current = null
    const bufferedEvents = bufferedTextEventsRef.current
    if (!bufferedEvents.length) return

    bufferedTextEventsRef.current = []
    coalesceTextEvents(bufferedEvents).forEach(({event, turnId}) =>
      applyEvents(turnId, [event]),
    )
  }, [applyEvents])

  const cancelBufferedTextEvents = useCallback((): void => {
    if (bufferedTextFlushIdRef.current !== null) {
      clearTimeout(bufferedTextFlushIdRef.current)
      bufferedTextFlushIdRef.current = null
    }
    bufferedTextEventsRef.current = []
  }, [])

  const applyEvent = useCallback(
    (turnId: string, event: DopamineRunEvent): void => {
      if (event.type === 'TEXT_MESSAGE_CONTENT') {
        bufferedTextEventsRef.current.push({event, turnId})
        if (bufferedTextFlushIdRef.current === null) {
          bufferedTextFlushIdRef.current = setTimeout(
            flushBufferedTextEvents,
            STREAM_RENDER_INTERVAL_MS,
          )
        }
        return
      }

      if (bufferedTextEventsRef.current.length) {
        if (bufferedTextFlushIdRef.current !== null) {
          clearTimeout(bufferedTextFlushIdRef.current)
        }
        flushBufferedTextEvents()
      }
      applyEvents(turnId, [event])
    },
    [applyEvents, flushBufferedTextEvents],
  )

  const failQueuedTurns = useCallback((message: string): void => {
    const queuedTurnIds = new Set(queueRef.current.map(({id}) => id))
    queueRef.current = []
    const occurredAt = Date.now()

    setTurns(currentTurns =>
      currentTurns.map(turn => {
        if (!queuedTurnIds.has(turn.id)) return turn

        return updateTurn(turn, {
          displayMessage: message,
          occurredAt,
          runId: turn.id,
          type: 'RUN_ERROR',
        })
      }),
    )
  }, [])

  const processTurn = useCallback(
    async (queuedTurn: QueuedTurn, generation: number): Promise<boolean> => {
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      const streamState: StreamRunState = {
        activeMessageId: null,
        hasRunStarted: false,
        hasTerminalEvent: false,
        isSessionExpired: false,
        runId: queuedTurn.id,
        threadId: '',
      }

      setTurns(currentTurns =>
        currentTurns.map(turn =>
          turn.id === queuedTurn.id ? {...turn, status: 'submitting'} : turn,
        ),
      )

      const runErrorMessage = t('DOPAMINE_RUN_ERROR')
      const sessionEndedMessage = t('DOPAMINE_SESSION_ENDED')

      try {
        await DopamineAssistantService.streamChat({
          clientSessionId,
          message: queuedTurn.submission.query,
          onEvent: (event: DopamineChatStreamEvent): void => {
            if (
              generationRef.current !== generation ||
              streamState.hasTerminalEvent
            )
              return

            handleChatEvent({
              event,
              onEvent: eventToApply => applyEvent(queuedTurn.id, eventToApply),
              onSessionExpired,
              onSessionStarted,
              runErrorMessage,
              sessionEndedMessage,
              state: streamState,
            })
          },
          signal: abortController.signal,
        })
      } catch {
        if (
          generationRef.current !== generation ||
          abortController.signal.aborted
        ) {
          return false
        }
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
      }

      if (generationRef.current !== generation) return false
      if (streamState.hasRunStarted && streamState.hasTerminalEvent) {
        onSessionSettled()
      }
      if (!streamState.hasTerminalEvent) {
        applyEvent(queuedTurn.id, {
          displayMessage: runErrorMessage,
          occurredAt: Date.now(),
          runId: streamState.runId,
          type: 'RUN_ERROR',
        })
      }
      if (streamState.isSessionExpired) {
        failQueuedTurns(sessionEndedMessage)
        return false
      }

      return true
    },
    [
      applyEvent,
      clientSessionId,
      failQueuedTurns,
      onSessionExpired,
      onSessionSettled,
      onSessionStarted,
      t,
    ],
  )

  const processQueue = useCallback(async (): Promise<void> => {
    if (isProcessingRef.current) return

    const generation = generationRef.current
    isProcessingRef.current = true

    while (generationRef.current === generation) {
      const queuedTurn = queueRef.current.shift()
      if (!queuedTurn) break

      const shouldContinue = await processTurn(queuedTurn, generation)
      if (!shouldContinue) break
    }

    if (generationRef.current === generation) isProcessingRef.current = false
  }, [processTurn])

  const cancelRuns = useCallback((): void => {
    generationRef.current += 1
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    queueRef.current = []
    isProcessingRef.current = false
    cancelBufferedTextEvents()
  }, [cancelBufferedTextEvents])

  const removeQueuedTurn = useCallback((turnId: string): void => {
    queueRef.current = queueRef.current.filter(turn => turn.id !== turnId)
    setTurns(currentTurns =>
      currentTurns.filter(
        turn => turn.id !== turnId || turn.status !== 'queued',
      ),
    )
  }, [])

  useEffect(() => {
    cancelRuns()
    setTurns([])
    return cancelRuns
  }, [cancelRuns, clientSessionId, conversationRevision])

  const submitPrompt = useCallback(
    (submission: DopaminePromptSubmission): void => {
      const existingQueuedTurn = queueRef.current[0]
      if (isProcessingRef.current && existingQueuedTurn) {
        const replacement = {...existingQueuedTurn, submission}
        queueRef.current = [replacement]
        setTurns(currentTurns =>
          currentTurns.map(turn =>
            turn.id === replacement.id ? {...turn, submission} : turn,
          ),
        )
        return
      }

      const queuedTurn = {
        id: `local-turn-${nextTurnIdRef.current}`,
        submission,
      }
      nextTurnIdRef.current += 1
      queueRef.current.push(queuedTurn)
      setTurns(currentTurns => [...currentTurns, createTurn(queuedTurn)])
      void processQueue()
    },
    [processQueue],
  )

  const queuedTurn = turns.find(turn => turn.status === 'queued') ?? null
  const isRunning = turns.some(
    turn =>
      turn.status === 'active' ||
      turn.status === 'queued' ||
      turn.status === 'submitting',
  )

  return {
    isRunning,
    queuedTurn,
    removeQueuedTurn,
    submitPrompt,
    turns,
  }
}
